/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import {
  $UserSubtenantFilterParams,
  GetUserSubtenantsData,
  GetUserSubtenantsResponse,
  UserSubtenant,
  UserSubtenantFilterParams,
  UserSubtenantSortableFields,
} from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { usePaginatedQuery } from '@/hooks/query/usePaginatedQuery';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { jsonSchemaToTypeMap } from '@/utils/filters';

export type UsePaginatedUserSubtenantsQueryParams = {
  params: Omit<GetUserSubtenantsData, 'tenantId'>;
  type?: 'inMemory' | 'queryParams';
  urlPrefix?: string;
  enabled?: boolean;
};

/**
 * Custom hook that fetches paginated user subtenants with filtering, sorting and real-time updates.
 * Combines React Query for data fetching with WebSocket subscriptions for real-time updates.
 *
 * @param options Configuration object:
 *   - params: Optional query parameters (filters, pagination, sort)
 *   - type: Storage type - 'inMemory' or 'queryParams' for URL persistence
 *
 * @returns Object containing:
 *   - data: Paginated user subtenants response
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - updateQueryParams: Function to update filters, sort, and pagination
 *
 * Note: tenantId is automatically pulled from the management plane state store
 */
export function usePaginatedUserSubtenantsQuery({
  params,
  type,
  urlPrefix,
  enabled,
}: UsePaginatedUserSubtenantsQueryParams) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  return usePaginatedQuery<
    GetUserSubtenantsResponse,
    UserSubtenant,
    UserSubtenantFilterParams,
    UserSubtenantSortableFields,
    GetUserSubtenantsData
  >({
    apiCall: mgmtPlaneAPIClient.users.getUserSubtenants.bind(mgmtPlaneAPIClient.users),
    filterTypes: jsonSchemaToTypeMap($UserSubtenantFilterParams),
    urlPrefix,
    queryKey: 'user-subtenants',
    realtimeEvents: ['subtenant-membership:updated'],
    params: {
      tenantId: selectedTenantId!,
      ...params,
    },
    type,
    enabled: enabled !== undefined ? enabled && !!selectedTenantId : !!selectedTenantId,
  });
}
